import { Line, ShapeType, Theme } from "../api/types";
import { OVAL_RX, OVAL_RY, SQUARE_HALF, DIAMOND_HALF, CLASSIC_CIRCLE_R, MODERN_OVAL_SCALE } from "./shapes";

const ARROW_OFFSET = 16.5;
const ARROW_LENGTH = 15;
const ARROW_WIDTH = 12;
const PERP_WIDTH = 25.5;
const WAVY_AMP = 3;
const WAVY_WAVELENGTH = 20;

function norm(dx: number, dy: number): [number, number] {
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  return [dx / len, dy / len];
}

/** Returns the effective start point for a line that originates from a shape's center. */
function attachmentEdgePoint(
  cx: number,
  cy: number,
  tx: number,
  ty: number,
  shapeType: ShapeType,
  rx: number,
  ry: number
): [number, number] {
  const [ux, uy] = norm(tx - cx, ty - cy);

  switch (shapeType) {
    case "receiverOval":
    case "linemanOval": {
      const t = 1 / Math.sqrt((ux / rx) ** 2 + (uy / ry) ** 2);
      return [cx + t * ux + 2 * ux, cy + t * uy + 2 * uy];
    }
    case "centerSquare": {
      const txFrac = Math.abs(ux) > 1e-9 ? rx / Math.abs(ux) : Infinity;
      const tyFrac = Math.abs(uy) > 1e-9 ? ry / Math.abs(uy) : Infinity;
      const t = Math.min(txFrac, tyFrac);
      return [cx + t * ux + 2 * ux, cy + t * uy + 2 * uy];
    }
    case "defenderDiamond": {
      const t = 1 / (Math.abs(ux) / rx + Math.abs(uy) / ry);
      return [cx + t * ux + 2 * ux, cy + t * uy + 2 * uy];
    }
  }
}

function shapeRadii(
  shapeType: ShapeType,
  modern: boolean,
  shapeSize?: { width: number; height: number }
): [number, number] {
  if (shapeSize) return [shapeSize.width / 2, shapeSize.height / 2];
  switch (shapeType) {
    case "receiverOval":
      return modern ? [OVAL_RX * MODERN_OVAL_SCALE, OVAL_RY * MODERN_OVAL_SCALE] : [CLASSIC_CIRCLE_R, CLASSIC_CIRCLE_R];
    case "linemanOval":
      return modern ? [OVAL_RX, OVAL_RY] : [CLASSIC_CIRCLE_R, CLASSIC_CIRCLE_R];
    case "centerSquare":
      return [SQUARE_HALF, SQUARE_HALF];
    case "defenderDiamond":
      return [DIAMOND_HALF, DIAMOND_HALF];
  }
}

function resolveStartPoint(line: Line, modern: boolean): [number, number] {
  if (line.attachedShapeId && line.attachedShapeType) {
    const [rx, ry] = shapeRadii(line.attachedShapeType, modern, line.attachedShapeSize);
    return attachmentEdgePoint(
      line.startPos.x, line.startPos.y,
      line.controlPoint.x, line.controlPoint.y,
      line.attachedShapeType,
      rx, ry
    );
  }
  return [line.startPos.x, line.startPos.y];
}

/** Quadratic Bézier tangent at t=1: B'(1) = 2*(P1 - CP) */
function finalTangent(line: Line, ex: number, ey: number): [number, number] {
  return norm(ex - line.controlPoint.x, ey - line.controlPoint.y);
}

function buildWavyPath(
  sx: number, sy: number,
  cpx: number, cpy: number,
  ex: number, ey: number
): string {
  const N = 80;
  const pts: [number, number][] = [];

  const arcLen = Math.sqrt((ex - sx) ** 2 + (ey - sy) ** 2);

  for (let i = 0; i <= N; i++) {
    const t = i / N;
    const mt = 1 - t;
    const px = mt * mt * sx + 2 * mt * t * cpx + t * t * ex;
    const py = mt * mt * sy + 2 * mt * t * cpy + t * t * ey;
    const dtx = 2 * (mt * (cpx - sx) + t * (ex - cpx));
    const dty = 2 * (mt * (cpy - sy) + t * (ey - cpy));
    const [perpX, perpY] = norm(-dty, dtx);
    const phase = (2 * Math.PI * (t * arcLen)) / WAVY_WAVELENGTH;
    const wave = WAVY_AMP * Math.sin(phase);
    pts.push([px + wave * perpX, py + wave * perpY]);
  }

  return "M " + pts.map(([x, y]) => `${x.toFixed(2)} ${y.toFixed(2)}`).join(" L ");
}

function buildEndMarker(
  line: Line,
  ex: number,
  ey: number,
  ux: number,
  uy: number
): string {
  const px = -uy;
  const py = ux;

  switch (line.strokeEnd) {
    case "straight":
      return "";

    case "arrow": {
      const tipX = ex;
      const tipY = ey;
      const bx = tipX - ux * ARROW_LENGTH;
      const by = tipY - uy * ARROW_LENGTH;
      const l1x = bx + px * (ARROW_WIDTH / 2);
      const l1y = by + py * (ARROW_WIDTH / 2);
      const l2x = bx - px * (ARROW_WIDTH / 2);
      const l2y = by - py * (ARROW_WIDTH / 2);
      return `<polygon points="${tipX},${tipY} ${l1x},${l1y} ${l2x},${l2y}" fill="${line.color}" stroke="none"/>`;
    }

    case "perpendicular": {
      const hw = PERP_WIDTH / 2;
      return `<line x1="${ex + px * hw}" y1="${ey + py * hw}" x2="${ex - px * hw}" y2="${ey - py * hw}"
        stroke="${line.color}" stroke-width="${line.strokeWidth}" stroke-linecap="round"/>`;
    }

    case "dotted": {
      const r = Math.min(Math.max(line.strokeWidth * 4.5, 10), 28) * 0.75;
      return `<circle cx="${ex}" cy="${ey}" r="${r / 2}" fill="${line.color}" stroke="none"/>`;
    }
  }
}

function strokeAttrs(line: Line): string {
  const dashArray =
    line.strokeType === "dashed"
      ? 'stroke-dasharray="10 10"'
      : line.strokeType === "dotted"
      ? 'stroke-dasharray="2 6"'
      : "";
  return `stroke="${line.color}" stroke-width="${line.strokeWidth}" fill="none" stroke-linecap="round" stroke-linejoin="round" ${dashArray}`;
}

export function buildLinesLayer(play: { line_list: Line[]; theme: Theme }): string {
  const elements: string[] = [];
  const modern = play.theme === "modern";

  for (const line of play.line_list) {
    const [sx, sy] = resolveStartPoint(line, modern);
    const { controlPoint: { x: cpx, y: cpy } } = line;

    const isArrow = line.strokeEnd === "arrow";
    const [ux, uy] = finalTangent(line, line.endPos.x, line.endPos.y);
    const ex = isArrow ? line.endPos.x - ux * ARROW_OFFSET : line.endPos.x;
    const ey = isArrow ? line.endPos.y - uy * ARROW_OFFSET : line.endPos.y;

    let pathD: string;
    if (line.strokeType === "wavy") {
      pathD = buildWavyPath(sx, sy, cpx, cpy, ex, ey);
    } else if (line.blockingScheme) {
      pathD = `M ${sx} ${sy} L ${cpx} ${cpy} L ${ex} ${ey}`;
    } else {
      pathD = `M ${sx} ${sy} Q ${cpx} ${cpy} ${ex} ${ey}`;
    }

    elements.push(`<path d="${pathD}" ${strokeAttrs(line)}/>`);

    const marker = buildEndMarker(line, line.endPos.x, line.endPos.y, ux, uy);
    if (marker) elements.push(marker);
  }

  return elements.join("\n");
}
